import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditingTables from "./editingTables";
import AddTableDialog from "./AddTableDialog";
import AddClientDialog from "./AddClientDialog";
import AppPageHeader from "../../../../../../components/AppPageHeader";
import EmptyState from "../../../../../../components/EmptyState";
import {
  createDatabaseFromSchema,
  createTable,
  getDatabaseSchema,
  getTableColumns,
  getTableRows,
  listRegisteredClients,
  listTables,
  registerClient,
  updateTableRow,
} from "./clientDataApi";

/**
 * TABLE SETS
 * ---------------------------------------------------------------------------
 * `dbSuffix` is the suffix used in the physical client database filename.
 * Example: DXP -> piping_dxpe.sqlite.
 *
 * Built-in clients stay here. Clients created from the UI are loaded from the
 * shared client registry and merged with this list.
 */
const BUILT_IN_TABLE_SETS = [
  { label: "General / Admin tables", value: "general", type: "admin" },
  { label: "AllFab", value: "allfab", type: "client", dbSuffix: "allfab" },
  { label: "CBL", value: "cbl", type: "client", dbSuffix: "cbl" },
  { label: "CWT", value: "cwt", type: "client", dbSuffix: "cwt" },
  { label: "DEMO", value: "demo", type: "client", dbSuffix: "demo" },
  // Existing client UI uses DXP while its database suffix is DXPE.
  { label: "DXP", value: "dxp", type: "client", dbSuffix: "dxpe" },
  { label: "ENVIRO", value: "enviro", type: "client", dbSuffix: "enviro" },
  { label: "PEI", value: "pei", type: "client", dbSuffix: "pei" },
  { label: "SWIRLTEX", value: "swirltex", type: "client", dbSuffix: "swirltex" },
];

const ADD_NEW_CLIENT = "__add_new_client__";

/**
 * DISCIPLINE DATABASE MAP
 * ---------------------------------------------------------------------------
 * adminDbName keeps the old General/Admin database names already used by the
 * application. clientDbPrefix builds <discipline>_<client>, for example:
 *   Piping + DXP -> piping_dxpe
 *
 * If another discipline's physical filename uses a different prefix, change
 * only clientDbPrefix here.
 */
const DISCIPLINES = [
  { label: "Piping", value: "piping", adminDbName: "piping", clientDbPrefix: "piping" },
  {
    label: "Structural",
    value: "structural",
    adminDbName: "structure",
    clientDbPrefix: "structure",
  },
  {
    label: "Pressure Vessel",
    value: "pressure-vessel",
    adminDbName: "pv",
    clientDbPrefix: "pv",
  },
  {
    label: "Storage Tank",
    value: "storage-tank",
    adminDbName: "tank",
    clientDbPrefix: "tank",
  },
  { label: "GA", value: "ga", adminDbName: "ga", clientDbPrefix: "ga" },
];

const ALL_TABLES = "All tables";

const classifyCategory = (tableName) => {
  const name = String(tableName || "");
  if (name.includes("#")) return "Dimensional Properties";
  if (/^Est_/i.test(name)) return "Estimation";
  return "Uncategorized";
};

const tableApi = {
  getItems: (tableName, dbName) => getTableRows(dbName, tableName),
  getColumns: (tableName, dbName) => getTableColumns(dbName, tableName),
  updateItem: (item, tableName, dbName) =>
    updateTableRow(dbName, tableName, item),
};

const resolveDatabaseName = (tableSet, discipline) => {
  if (!tableSet || !discipline) return "";
  if (tableSet.type === "admin") return discipline.adminDbName;
  return `${discipline.clientDbPrefix}_${tableSet.dbSuffix}`;
};

export default function ClientDataEditingPage() {
  const [tableSets, setTableSets] = useState(BUILT_IN_TABLE_SETS);
  const [selectedTableSet, setSelectedTableSet] = useState(
    BUILT_IN_TABLE_SETS[0].value
  );
  const [selectedDiscipline, setSelectedDiscipline] = useState(DISCIPLINES[0].value);
  const [selectedCategory, setSelectedCategory] = useState(ALL_TABLES);
  const [selectedTable, setSelectedTable] = useState("");
  const [tableOptions, setTableOptions] = useState([]);
  const [loadingTables, setLoadingTables] = useState(false);
  const [databaseError, setDatabaseError] = useState("");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addClientDialogOpen, setAddClientDialogOpen] = useState(false);
  const [clientRegistryWarning, setClientRegistryWarning] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const tableSet = useMemo(
    () => tableSets.find((item) => item.value === selectedTableSet),
    [selectedTableSet, tableSets]
  );

  const discipline = useMemo(
    () => DISCIPLINES.find((item) => item.value === selectedDiscipline),
    [selectedDiscipline]
  );

  const dbName = useMemo(
    () => resolveDatabaseName(tableSet, discipline),
    [tableSet, discipline]
  );

  useEffect(() => {
    let active = true;

    const loadRegisteredClients = async () => {
      try {
        const registeredClients = await listRegisteredClients();
        if (!active) return;

        setTableSets(() => {
          const builtInValues = new Set(
            BUILT_IN_TABLE_SETS.map((item) => item.value)
          );
          const builtInSuffixes = new Set(
            BUILT_IN_TABLE_SETS.filter((item) => item.dbSuffix).map(
              (item) => item.dbSuffix
            )
          );
          const uniqueRegisteredClients = registeredClients.filter(
            (item) =>
              !builtInValues.has(item.value) &&
              !builtInSuffixes.has(item.dbSuffix)
          );
          return [...BUILT_IN_TABLE_SETS, ...uniqueRegisteredClients];
        });
        setClientRegistryWarning("");
      } catch (error) {
        console.error("Failed to load the client registry:", error);
        if (active) {
          setClientRegistryWarning(
            "Saved custom clients could not be loaded. Built-in clients are still available."
          );
        }
      }
    };

    loadRegisteredClients();
    return () => {
      active = false;
    };
  }, []);

  const categories = useMemo(() => {
    const categorySet = new Set(tableOptions.map(classifyCategory));
    return [ALL_TABLES, ...Array.from(categorySet).sort()];
  }, [tableOptions]);

  const filteredTables = useMemo(() => {
    if (selectedCategory === ALL_TABLES) return tableOptions;
    return tableOptions.filter(
      (tableName) => classifyCategory(tableName) === selectedCategory
    );
  }, [selectedCategory, tableOptions]);

  const loadTables = useCallback(
    async (databaseName = dbName) => {
      if (!databaseName) {
        setTableOptions([]);
        return [];
      }

      try {
        setLoadingTables(true);
        setDatabaseError("");
        const names = await listTables(databaseName);
        const sortedNames = [...names].sort((a, b) => a.localeCompare(b));
        setTableOptions(sortedNames);
        return sortedNames;
      } catch (error) {
        console.error(`Failed to list tables for "${databaseName}":`, error);
        setTableOptions([]);
        setDatabaseError(
          `Could not load database "${databaseName}". Check that the database exists and is registered by the API.`
        );
        return [];
      } finally {
        setLoadingTables(false);
      }
    },
    [dbName]
  );

  useEffect(() => {
    setSelectedCategory(ALL_TABLES);
    setSelectedTable("");
    loadTables(dbName);
  }, [dbName, loadTables]);

  useEffect(() => {
    if (selectedTable && !filteredTables.includes(selectedTable)) {
      setSelectedTable("");
    }
  }, [filteredTables, selectedTable]);

  const handleTableCreated = async (tableName) => {
    setSelectedCategory(ALL_TABLES);
    const names = await loadTables(dbName);
    if (names.includes(tableName)) setSelectedTable(tableName);
    setSuccessMessage(`Table "${tableName}" created in ${dbName}.`);
  };

  const handleCreateClient = async (client, setProgressMessage) => {
    const templateSet = tableSets.find(
      (item) => item.value === client.templateTableSet
    );
    const selectedDisciplines = DISCIPLINES.filter((item) =>
      client.disciplineValues.includes(item.value)
    );

    if (!templateSet) throw new Error("The selected template client was not found.");
    if (!selectedDisciplines.length) {
      throw new Error("Select at least one discipline database.");
    }

    const databasePlans = [];
    for (let index = 0; index < selectedDisciplines.length; index += 1) {
      const selectedDisciplineItem = selectedDisciplines[index];
      const sourceDbName = resolveDatabaseName(templateSet, selectedDisciplineItem);
      const targetDbName = resolveDatabaseName(
        { type: "client", dbSuffix: client.dbSuffix },
        selectedDisciplineItem
      );

      setProgressMessage(
        `Checking ${selectedDisciplineItem.label} template (${index + 1}/${
          selectedDisciplines.length
        })...`
      );

      const schema = await getDatabaseSchema(sourceDbName);
      if (!schema.length) {
        throw new Error(
          `Template database "${sourceDbName}" has no schema to copy.`
        );
      }

      databasePlans.push({
        discipline: selectedDisciplineItem,
        sourceDbName,
        targetDbName,
        schema,
      });
    }

    const completedDatabases = [];
    try {
      for (let index = 0; index < databasePlans.length; index += 1) {
        const plan = databasePlans[index];
        await createDatabaseFromSchema(
          plan.targetDbName,
          plan.schema,
          ({ current, total, name }) => {
            setProgressMessage(
              `Creating ${plan.discipline.label} database (${index + 1}/${
                databasePlans.length
              }): ${name} (${current}/${total})`
            );
          }
        );
        completedDatabases.push(plan.targetDbName);
      }

      setProgressMessage("Saving the client registration...");
      await registerClient(client);
    } catch (error) {
      if (completedDatabases.length) {
        throw new Error(
          `${error?.response?.data?.message || error?.message || "Client creation failed."} ` +
            `Created before the failure: ${completedDatabases.join(", ")}.`
        );
      }
      throw error;
    }

    const createdTableSet = {
      label: client.label,
      value: client.value,
      type: "client",
      dbSuffix: client.dbSuffix,
      userCreated: true,
    };

    setTableSets((current) =>
      [...current, createdTableSet].sort((a, b) => {
        if (a.type === "admin") return -1;
        if (b.type === "admin") return 1;
        return a.label.localeCompare(b.label);
      })
    );
    setSelectedTableSet(createdTableSet.value);
    if (!client.disciplineValues.includes(selectedDiscipline)) {
      setSelectedDiscipline(client.disciplineValues[0]);
    }
    setClientRegistryWarning("");
    setSuccessMessage(
      `Client "${client.label}" created with ${databasePlans.length} empty discipline database${
        databasePlans.length === 1 ? "" : "s"
      }.`
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: { xs: 2, md: 3 },
      }}
    >
      <AppPageHeader
        moduleKey="client-data"
        title="Client Data"
        subtitle="Each client's standard tables - pick a table set, discipline and table to view or edit it."
      />
      <Paper
        sx={{
          width: "100%",
          p: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <FormControl size="small" sx={{ minWidth: 275 }}>
          <InputLabel>Table set</InputLabel>
          <Select
            label="Table set"
            value={selectedTableSet}
            onChange={(event) => {
              if (event.target.value === ADD_NEW_CLIENT) {
                setAddClientDialogOpen(true);
                return;
              }
              setSelectedTableSet(event.target.value);
            }}
          >
            {tableSets.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
            <MenuItem
              value={ADD_NEW_CLIENT}
              divider
              sx={{ color: "secondary.main", fontWeight: 600, gap: 1 }}
            >
              <AddIcon fontSize="small" />
              Add new client
            </MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel>Discipline</InputLabel>
          <Select
            label="Discipline"
            value={selectedDiscipline}
            onChange={(event) => setSelectedDiscipline(event.target.value)}
          >
            {DISCIPLINES.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 250 }} disabled={loadingTables}>
          <InputLabel>Category</InputLabel>
          <Select
            label="Category"
            value={selectedCategory}
            onChange={(event) => {
              setSelectedCategory(event.target.value);
              setSelectedTable("");
            }}
          >
            {categories.map((category) => (
              <MenuItem key={category} value={category}>
                {category}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 350 }} disabled={loadingTables}>
          <InputLabel>Table</InputLabel>
          <Select
            label="Table"
            value={selectedTable}
            onChange={(event) => setSelectedTable(event.target.value)}
            endAdornment={
              loadingTables ? <CircularProgress size={18} sx={{ mr: 3 }} /> : null
            }
          >
            {filteredTables.map((tableName) => (
              <MenuItem key={tableName} value={tableName}>
                {tableName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          color="secondary"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
          disabled={!dbName || loadingTables || Boolean(databaseError)}
          sx={{ ml: { xs: 0, xl: "auto" }, minWidth: 140, height: 40 }}
        >
          Add table
        </Button>
      </Paper>

      {databaseError && <Alert severity="error">{databaseError}</Alert>}
      {clientRegistryWarning && (
        <Alert severity="warning">{clientRegistryWarning}</Alert>
      )}

      {!databaseError && !selectedTable && (
        <EmptyState
          moduleKey="client-data"
          title="No table selected"
          description="Select a table above to view and edit its data."
        />
      )}

      {!databaseError && selectedTable && (
        <EditingTables
          selectedTable={selectedTable}
          dbName={dbName}
          tableApi={tableApi}
          enableEdit
        />
      )}

      <AddTableDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        dbName={dbName}
        tableOptions={tableOptions}
        getColumns={getTableColumns}
        createTable={createTable}
        onCreated={handleTableCreated}
      />

      <AddClientDialog
        open={addClientDialogOpen}
        onClose={() => setAddClientDialogOpen(false)}
        tableSets={tableSets}
        disciplines={DISCIPLINES}
        onCreate={handleCreateClient}
      />

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={3500}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
